/**
 * Hook para monitoramento em tempo real da distribuição automática
 * 
 * VERSÃO 2.0.0 - Suporta múltiplos tipos de entidades (conversation, deal)
 * 
 * Funcionalidades:
 * - Busca métricas agregadas de distribuição (hoje, taxa de sucesso, etc.)
 * - Lista status de agentes online com contagem de conversas e deals
 * - Exibe departamentos e filas globais com distribuição ativa
 * - Streaming de distribuições recentes via Supabase Realtime
 * - Escuta mudanças de status de agentes em tempo real
 * - Filtro por tipo de entidade (conversation, deal, all)
 * 
 * @version 2.0.0
 * @date 2025-01-20
 */

import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { logger } from '@/lib/logger';

// ==============================================================
// Interfaces e Tipos
// ==============================================================

/** Tipos de entidade suportados */
export type EntityType = 'conversation' | 'deal' | 'all';

/** Tipos de conversa (para compatibilidade) */
export type ConversationType = 'individual' | 'group';

/** Métricas agregadas de distribuição */
export interface DistributionStats {
  /** Total de distribuições hoje */
  distributedToday: number;
  /** Taxa de sucesso (%) */
  successRate: number;
  /** Agentes online */
  agentsOnline: number;
  /** Itens aguardando distribuição */
  pendingDistribution: number;
  /** Tempo médio de processamento (ms) */
  averageProcessingTime: number;
  
  // === Métricas por tipo de conversa (compatibilidade) ===
  /** Distribuições individuais hoje */
  distributedTodayIndividual: number;
  /** Distribuições de grupos hoje */
  distributedTodayGroup: number;
  /** Conversas individuais aguardando */
  pendingIndividual: number;
  /** Conversas de grupos aguardando */
  pendingGroup: number;
  
  // === Métricas por tipo de entidade (novo) ===
  /** Distribuições de conversas hoje */
  distributedTodayConversation: number;
  /** Distribuições de deals hoje */
  distributedTodayDeal: number;
  /** Conversas aguardando */
  pendingConversation: number;
  /** Deals aguardando */
  pendingDeal: number;
}

/** Status de um agente para o dashboard */
export interface AgentStatus {
  userId: string;
  fullName: string;
  email: string;
  avatarUrl: string | null;
  onlineStatus: 'online' | 'away' | 'busy' | 'offline';
  availableForDistribution: boolean;
  activeConversations: number;
  activeDeals: number; // Novo: deals ativos
  lastSeenAt: string | null;
}

/** Departamento com distribuição ativa */
export interface DepartmentStatus {
  id: string;
  name: string;
  distributionMethod: string;
  maxConversationsPerAgent: number;
  agentsCount: number;
  onlineAgentsCount: number;
  lastAssignedAgentName: string | null;
}

/** Fila global de distribuição */
export interface QueueStatus {
  id: string;
  name: string;
  contextType: 'chat' | 'crm' | 'all';
  targetType: 'department' | 'workspace' | 'pipeline' | 'global';
  distributionMethod: string;
  maxItemsPerAgent: number;
  membersCount: number;
  onlineMembersCount: number;
  isActive: boolean;
}

/** Log de distribuição recente - expandido para suportar deals */
export interface RecentDistribution {
  id: string;
  // Campos de entidade genérica
  entityType: 'conversation' | 'deal';
  entityId: string;
  
  // Campos legados para conversas (compatibilidade)
  conversationId: string;
  departmentName: string;
  
  // Campos de fila global (novo)
  queueId: string | null;
  queueName: string | null;
  
  // Dados do agente e distribuição
  agentName: string | null;
  contactName: string | null;
  contactPhone: string | null;
  distributionMethod: string;
  eligibleAgentsCount: number;
  processingTimeMs: number;
  success: boolean;
  failureReason: string | null;
  createdAt: string;
  
  // Campos de tipo de conversa (compatibilidade)
  conversationType: 'individual' | 'group';
  groupId: string | null;
  groupName: string | null;
  
  // Campos específicos de deals (novo)
  dealTitle: string | null;
}

/** Dados de volume por hora para gráfico */
export interface HourlyVolume {
  hour: number;
  count: number;
  successCount: number;
  failureCount: number;
  // Novo: breakdown por tipo de entidade
  conversationCount: number;
  dealCount: number;
}

// ==============================================================
// Hook Principal
// ==============================================================

export function useDistributionMonitor() {
  const { currentTenant } = useAuth();
  const tenantId = currentTenant?.id;

  // Estados principais
  const [stats, setStats] = useState<DistributionStats>({
    distributedToday: 0,
    successRate: 0,
    agentsOnline: 0,
    pendingDistribution: 0,
    averageProcessingTime: 0,
    // Métricas por tipo de conversa (compatibilidade)
    distributedTodayIndividual: 0,
    distributedTodayGroup: 0,
    pendingIndividual: 0,
    pendingGroup: 0,
    // Métricas por tipo de entidade
    distributedTodayConversation: 0,
    distributedTodayDeal: 0,
    pendingConversation: 0,
    pendingDeal: 0,
  });
  const [agentStatuses, setAgentStatuses] = useState<AgentStatus[]>([]);
  const [departments, setDepartments] = useState<DepartmentStatus[]>([]);
  const [queues, setQueues] = useState<QueueStatus[]>([]);
  const [recentDistributions, setRecentDistributions] = useState<RecentDistribution[]>([]);
  const [hourlyVolume, setHourlyVolume] = useState<HourlyVolume[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // Estado do filtro por tipo de conversa (compatibilidade)
  const [typeFilter, setTypeFilter] = useState<'all' | 'individual' | 'group'>('all');
  
  // Estado do filtro por tipo de entidade (novo)
  const [entityFilter, setEntityFilter] = useState<EntityType>('all');

  // ==============================================================
  // Busca de Dados
  // ==============================================================

  /** Busca métricas agregadas de distribuição */
  const fetchStats = useCallback(async () => {
    if (!tenantId) return;

    try {
      // Início do dia atual em UTC
      const todayStart = new Date();
      todayStart.setHours(0, 0, 0, 0);

      // Busca logs de distribuição de hoje COM entity_type e conversation_type
      const { data: logs, error: logsError } = await supabase
        .from('distribution_logs')
        .select('success, processing_time_ms, conversation_type, entity_type')
        .eq('tenant_id', tenantId)
        .gte('created_at', todayStart.toISOString());

      if (logsError) throw logsError;

      // Busca agentes online
      const { data: onlineAgents, error: agentsError } = await supabase
        .from('tenant_members')
        .select('user_id')
        .eq('tenant_id', tenantId)
        .eq('is_active', true)
        .eq('online_status', 'online');

      if (agentsError) throw agentsError;

      // Busca conversas aguardando distribuição (em departamento mas sem agente)
      const { data: pendingConvs, error: pendingError } = await supabase
        .from('conversations')
        .select('id, group_id, inbox:inboxes!inner(tenant_id)')
        .not('department_id', 'is', null)
        .is('assigned_to', null)
        .in('status', ['pending', 'open']);

      if (pendingError) throw pendingError;

      // Busca deals aguardando distribuição (status pending)
      const { data: pendingDeals, error: pendingDealsError } = await supabase
        .from('crm_deals')
        .select('id')
        .eq('tenant_id', tenantId)
        .eq('distribution_status', 'pending')
        .is('assigned_to', null);

      if (pendingDealsError) throw pendingDealsError;

      // Filtra conversas do tenant atual
      const pendingInTenant = pendingConvs?.filter(
        (c: any) => c.inbox?.tenant_id === tenantId
      ) || [];

      // Separa métricas por tipo de conversa
      const individualLogs = logs?.filter(l => l.conversation_type !== 'group') || [];
      const groupLogs = logs?.filter(l => l.conversation_type === 'group') || [];
      
      // Separa métricas por tipo de entidade
      const conversationLogs = logs?.filter(l => !l.entity_type || l.entity_type === 'conversation') || [];
      const dealLogs = logs?.filter(l => l.entity_type === 'deal') || [];
      
      const pendingIndividual = pendingInTenant.filter((c: any) => !c.group_id);
      const pendingGroup = pendingInTenant.filter((c: any) => c.group_id);

      // Calcula métricas
      const totalLogs = logs?.length || 0;
      const successLogs = logs?.filter(l => l.success)?.length || 0;
      const avgProcessing = totalLogs > 0
        ? Math.round((logs?.reduce((sum, l) => sum + (l.processing_time_ms || 0), 0) || 0) / totalLogs)
        : 0;

      setStats({
        distributedToday: totalLogs,
        successRate: totalLogs > 0 ? Math.round((successLogs / totalLogs) * 100) : 100,
        agentsOnline: onlineAgents?.length || 0,
        pendingDistribution: pendingInTenant.length + (pendingDeals?.length || 0),
        averageProcessingTime: avgProcessing,
        // Métricas por tipo de conversa
        distributedTodayIndividual: individualLogs.length,
        distributedTodayGroup: groupLogs.length,
        pendingIndividual: pendingIndividual.length,
        pendingGroup: pendingGroup.length,
        // Métricas por tipo de entidade
        distributedTodayConversation: conversationLogs.length,
        distributedTodayDeal: dealLogs.length,
        pendingConversation: pendingInTenant.length,
        pendingDeal: pendingDeals?.length || 0,
      });

    } catch (err) {
      logger.error('DistributionMonitor', 'Erro ao buscar stats', err);
      setError('Falha ao carregar métricas');
    }
  }, [tenantId]);

  /** Busca status de todos os agentes do tenant */
  const fetchAgentStatuses = useCallback(async () => {
    if (!tenantId) return;

    try {
      // Busca membros do tenant com perfil
      const { data: members, error: membersError } = await supabase
        .from('tenant_members')
        .select(`
          user_id,
          online_status,
          available_for_distribution,
          last_seen_at,
          profile:profiles!inner(full_name, email, avatar_url)
        `)
        .eq('tenant_id', tenantId)
        .eq('is_active', true);

      if (membersError) throw membersError;

      // Busca contagem de conversas por agente
      const agentIds = members?.map(m => m.user_id) || [];
      
      if (agentIds.length === 0) {
        setAgentStatuses([]);
        return;
      }

      const { data: convCounts, error: convError } = await supabase
        .from('conversations')
        .select('assigned_to')
        .in('assigned_to', agentIds)
        .in('status', ['pending', 'open']);

      if (convError) throw convError;

      // Busca contagem de deals por agente
      const { data: dealCounts, error: dealError } = await supabase
        .from('crm_deals')
        .select('assigned_to')
        .in('assigned_to', agentIds)
        .not('status', 'in', '("won","lost","cancelled")');

      if (dealError) throw dealError;

      // Agrupa contagem por agente
      const convCountMap: Record<string, number> = {};
      convCounts?.forEach(c => {
        if (c.assigned_to) {
          convCountMap[c.assigned_to] = (convCountMap[c.assigned_to] || 0) + 1;
        }
      });

      const dealCountMap: Record<string, number> = {};
      dealCounts?.forEach(d => {
        if (d.assigned_to) {
          dealCountMap[d.assigned_to] = (dealCountMap[d.assigned_to] || 0) + 1;
        }
      });

      // Monta lista de status
      const statuses: AgentStatus[] = (members || []).map(m => ({
        userId: m.user_id,
        fullName: (m.profile as any)?.full_name || 'Sem nome',
        email: (m.profile as any)?.email || '',
        avatarUrl: (m.profile as any)?.avatar_url,
        onlineStatus: (m.online_status || 'offline') as AgentStatus['onlineStatus'],
        availableForDistribution: m.available_for_distribution || false,
        activeConversations: convCountMap[m.user_id] || 0,
        activeDeals: dealCountMap[m.user_id] || 0,
        lastSeenAt: m.last_seen_at,
      }));

      // Ordena: online primeiro, depois por nome
      statuses.sort((a, b) => {
        const statusOrder = { online: 0, away: 1, busy: 2, offline: 3 };
        const orderDiff = statusOrder[a.onlineStatus] - statusOrder[b.onlineStatus];
        if (orderDiff !== 0) return orderDiff;
        return a.fullName.localeCompare(b.fullName);
      });

      setAgentStatuses(statuses);

    } catch (err) {
      logger.error('DistributionMonitor', 'Erro ao buscar agentes', err);
    }
  }, [tenantId]);

  /** Busca departamentos com distribuição ativa */
  const fetchDepartments = useCallback(async () => {
    if (!tenantId) return;

    try {
      // Busca departamentos ativos com distribuição
      const { data: depts, error: deptsError } = await supabase
        .from('departments')
        .select(`
          id,
          name,
          distribution_method,
          max_conversations_per_agent,
          last_assigned_agent_id,
          department_agents(user_id)
        `)
        .eq('tenant_id', tenantId)
        .eq('is_active', true)
        .eq('auto_distribution_enabled', true);

      if (deptsError) throw deptsError;

      if (!depts || depts.length === 0) {
        setDepartments([]);
        return;
      }

      // Busca agentes online por departamento
      const allAgentIds = depts.flatMap(d => 
        (d.department_agents as any[])?.map((da: any) => da.user_id) || []
      );

      const { data: onlineMembers, error: membersError } = await supabase
        .from('tenant_members')
        .select('user_id, online_status')
        .eq('tenant_id', tenantId)
        .in('user_id', allAgentIds);

      if (membersError) throw membersError;

      const onlineSet = new Set(
        (onlineMembers || [])
          .filter(m => m.online_status === 'online')
          .map(m => m.user_id)
      );

      // Busca nomes dos últimos agentes atribuídos
      const lastAgentIds = depts
        .map(d => d.last_assigned_agent_id)
        .filter(Boolean) as string[];

      let agentNames: Record<string, string> = {};
      if (lastAgentIds.length > 0) {
        const { data: profiles } = await supabase
          .from('profiles')
          .select('id, full_name')
          .in('id', lastAgentIds);

        profiles?.forEach(p => {
          agentNames[p.id] = p.full_name || 'Sem nome';
        });
      }

      // Monta lista de departamentos
      const deptStatuses: DepartmentStatus[] = depts.map(d => {
        const agentIds = (d.department_agents as any[])?.map((da: any) => da.user_id) || [];
        const onlineCount = agentIds.filter(id => onlineSet.has(id)).length;

        return {
          id: d.id,
          name: d.name,
          distributionMethod: d.distribution_method || 'round_robin',
          maxConversationsPerAgent: d.max_conversations_per_agent || 0,
          agentsCount: agentIds.length,
          onlineAgentsCount: onlineCount,
          lastAssignedAgentName: d.last_assigned_agent_id 
            ? agentNames[d.last_assigned_agent_id] || null 
            : null,
        };
      });

      setDepartments(deptStatuses);

    } catch (err) {
      logger.error('DistributionMonitor', 'Erro ao buscar departamentos', err);
    }
  }, [tenantId]);

  /** Busca filas globais de distribuição */
  const fetchQueues = useCallback(async () => {
    if (!tenantId) return;

    try {
      // Busca filas ativas do tenant
      const { data: queueData, error: queuesError } = await supabase
        .from('distribution_queues')
        .select(`
          id,
          name,
          context_type,
          target_type,
          distribution_method,
          max_items_per_agent,
          is_active,
          distribution_queue_members(user_id, is_active)
        `)
        .eq('tenant_id', tenantId)
        .eq('is_active', true);

      if (queuesError) throw queuesError;

      if (!queueData || queueData.length === 0) {
        setQueues([]);
        return;
      }

      // Busca membros online
      const allMemberIds = queueData.flatMap(q => 
        (q.distribution_queue_members as any[])?.map((m: any) => m.user_id) || []
      );

      const { data: onlineMembers } = await supabase
        .from('tenant_members')
        .select('user_id, online_status')
        .eq('tenant_id', tenantId)
        .in('user_id', allMemberIds);

      const onlineSet = new Set(
        (onlineMembers || [])
          .filter(m => m.online_status === 'online')
          .map(m => m.user_id)
      );

      // Monta lista de filas
      const queueStatuses: QueueStatus[] = queueData.map(q => {
        const members = (q.distribution_queue_members as any[])?.filter((m: any) => m.is_active) || [];
        const onlineCount = members.filter((m: any) => onlineSet.has(m.user_id)).length;

        return {
          id: q.id,
          name: q.name,
          contextType: q.context_type as 'chat' | 'crm' | 'all',
          targetType: q.target_type as 'department' | 'workspace' | 'pipeline' | 'global',
          distributionMethod: q.distribution_method || 'round_robin',
          maxItemsPerAgent: q.max_items_per_agent || 0,
          membersCount: members.length,
          onlineMembersCount: onlineCount,
          isActive: q.is_active,
        };
      });

      setQueues(queueStatuses);

    } catch (err) {
      logger.error('DistributionMonitor', 'Erro ao buscar filas', err);
    }
  }, [tenantId]);

  /** Busca distribuições recentes - expandido para entity_type */
  const fetchRecentDistributions = useCallback(async () => {
    if (!tenantId) return;

    try {
      // Inclui entity_type, entity_id e queue_id na query
      const { data: logs, error: logsError } = await supabase
        .from('distribution_logs')
        .select(`
          id,
          conversation_id,
          department_id,
          assigned_agent_id,
          distribution_method,
          eligible_agents_count,
          processing_time_ms,
          success,
          failure_reason,
          created_at,
          conversation_type,
          group_id,
          entity_type,
          entity_id,
          queue_id
        `)
        .eq('tenant_id', tenantId)
        .order('created_at', { ascending: false })
        .limit(30);

      if (logsError) throw logsError;

      if (!logs || logs.length === 0) {
        setRecentDistributions([]);
        return;
      }

      // Busca nomes dos departamentos
      const deptIds = [...new Set(logs.map(l => l.department_id).filter(Boolean))];
      let deptMap: Record<string, string> = {};
      if (deptIds.length > 0) {
        const { data: depts } = await supabase
          .from('departments')
          .select('id, name')
          .in('id', deptIds);
        depts?.forEach(d => { deptMap[d.id] = d.name; });
      }

      // Busca nomes das filas
      const queueIds = [...new Set(logs.map(l => l.queue_id).filter(Boolean))];
      let queueMap: Record<string, string> = {};
      if (queueIds.length > 0) {
        const { data: queuesData } = await supabase
          .from('distribution_queues')
          .select('id, name')
          .in('id', queueIds);
        queuesData?.forEach(q => { queueMap[q.id] = q.name; });
      }

      // Busca nomes dos agentes
      const agentIds = logs.map(l => l.assigned_agent_id).filter(Boolean) as string[];
      let agentMap: Record<string, string> = {};
      if (agentIds.length > 0) {
        const { data: profiles } = await supabase
          .from('profiles')
          .select('id, full_name')
          .in('id', agentIds);
        profiles?.forEach(p => { agentMap[p.id] = p.full_name || 'Sem nome'; });
      }

      // Busca dados das conversas (contato)
      const convIds = logs.filter(l => l.conversation_id).map(l => l.conversation_id);
      let contactMap: Record<string, { name: string | null; phone: string | null }> = {};
      if (convIds.length > 0) {
        const { data: convs } = await supabase
          .from('conversations')
          .select('id, contact:contacts(name, phone)')
          .in('id', convIds);

        convs?.forEach(c => {
          const contact = c.contact as any;
          contactMap[c.id] = {
            name: contact?.name || null,
            phone: contact?.phone || null,
          };
        });
      }

      // Busca dados dos deals
      const dealIds = logs.filter(l => l.entity_type === 'deal' && l.entity_id).map(l => l.entity_id);
      let dealMap: Record<string, { title: string; contactName: string | null; contactPhone: string | null }> = {};
      if (dealIds.length > 0) {
        const { data: deals } = await supabase
          .from('crm_deals')
          .select('id, title, contact:contacts(name, phone)')
          .in('id', dealIds);

        deals?.forEach(d => {
          const contact = d.contact as any;
          dealMap[d.id] = {
            title: d.title,
            contactName: contact?.name || null,
            contactPhone: contact?.phone || null,
          };
        });
      }

      // Busca nomes dos grupos (se houver distribuições de grupo)
      const groupIds = logs.map(l => l.group_id).filter(Boolean) as string[];
      let groupMap: Record<string, string> = {};
      if (groupIds.length > 0) {
        // RPC server-side (substitui SELECT direto em chat_groups)
        const { data: groups } = await supabase.rpc('get_chat_groups_names_safe', {
          p_group_ids: groupIds,
        });
        groups?.forEach(g => { groupMap[g.id] = g.name || 'Grupo'; });
      }

      // Monta lista com campos de tipo expandidos
      const distributions: RecentDistribution[] = logs.map(l => {
        const entityType = (l.entity_type || 'conversation') as 'conversation' | 'deal';
        const entityId = l.entity_id || l.conversation_id;
        
        // Dados do contato baseado no tipo de entidade
        let contactName: string | null = null;
        let contactPhone: string | null = null;
        let dealTitle: string | null = null;
        
        if (entityType === 'deal' && l.entity_id && dealMap[l.entity_id]) {
          const dealData = dealMap[l.entity_id];
          dealTitle = dealData.title;
          contactName = dealData.contactName;
          contactPhone = dealData.contactPhone;
        } else if (l.conversation_id && contactMap[l.conversation_id]) {
          contactName = contactMap[l.conversation_id].name;
          contactPhone = contactMap[l.conversation_id].phone;
        }

        return {
          id: l.id,
          entityType,
          entityId,
          conversationId: l.conversation_id || '',
          departmentName: l.department_id ? (deptMap[l.department_id] || 'Departamento') : '',
          queueId: l.queue_id || null,
          queueName: l.queue_id ? (queueMap[l.queue_id] || 'Fila') : null,
          agentName: l.assigned_agent_id ? agentMap[l.assigned_agent_id] || null : null,
          contactName,
          contactPhone,
          distributionMethod: l.distribution_method,
          eligibleAgentsCount: l.eligible_agents_count,
          processingTimeMs: l.processing_time_ms || 0,
          success: l.success,
          failureReason: l.failure_reason,
          createdAt: l.created_at,
          conversationType: (l.conversation_type === 'group' ? 'group' : 'individual') as 'individual' | 'group',
          groupId: l.group_id || null,
          groupName: l.group_id ? groupMap[l.group_id] || 'Grupo' : null,
          dealTitle,
        };
      });

      setRecentDistributions(distributions);

    } catch (err) {
      logger.error('DistributionMonitor', 'Erro ao buscar distribuições recentes', err);
    }
  }, [tenantId]);

  /** Busca volume de distribuições por hora (últimas 24h) */
  const fetchHourlyVolume = useCallback(async () => {
    if (!tenantId) return;

    try {
      const yesterday = new Date();
      yesterday.setHours(yesterday.getHours() - 24);

      const { data: logs, error: logsError } = await supabase
        .from('distribution_logs')
        .select('created_at, success, entity_type')
        .eq('tenant_id', tenantId)
        .gte('created_at', yesterday.toISOString());

      if (logsError) throw logsError;

      // Agrupa por hora
      const hourMap: Record<number, { count: number; success: number; failure: number; conversation: number; deal: number }> = {};
      
      // Inicializa todas as horas
      for (let i = 0; i < 24; i++) {
        hourMap[i] = { count: 0, success: 0, failure: 0, conversation: 0, deal: 0 };
      }

      logs?.forEach(l => {
        const hour = new Date(l.created_at).getHours();
        hourMap[hour].count++;
        if (l.success) {
          hourMap[hour].success++;
        } else {
          hourMap[hour].failure++;
        }
        // Contagem por tipo de entidade
        if (l.entity_type === 'deal') {
          hourMap[hour].deal++;
        } else {
          hourMap[hour].conversation++;
        }
      });

      const volume: HourlyVolume[] = Object.entries(hourMap).map(([hour, data]) => ({
        hour: parseInt(hour),
        count: data.count,
        successCount: data.success,
        failureCount: data.failure,
        conversationCount: data.conversation,
        dealCount: data.deal,
      }));

      setHourlyVolume(volume);

    } catch (err) {
      logger.error('DistributionMonitor', 'Erro ao buscar volume por hora', err);
    }
  }, [tenantId]);

  /** Busca todos os dados */
  const fetchAll = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    await Promise.all([
      fetchStats(),
      fetchAgentStatuses(),
      fetchDepartments(),
      fetchQueues(),
      fetchRecentDistributions(),
      fetchHourlyVolume(),
    ]);

    setIsLoading(false);
  }, [fetchStats, fetchAgentStatuses, fetchDepartments, fetchQueues, fetchRecentDistributions, fetchHourlyVolume]);

  /** Refresh manual */
  const refresh = useCallback(() => {
    fetchAll();
  }, [fetchAll]);

  // ==============================================================
  // Efeitos e Subscriptions
  // ==============================================================

  // Busca inicial
  useEffect(() => {
    if (tenantId) {
      fetchAll();
    }
  }, [tenantId, fetchAll]);

  // Subscription para novas distribuições (real-time)
  useEffect(() => {
    if (!tenantId) return;

    logger.info('DistributionMonitor', 'Iniciando subscription real-time para distribuições');

    const channel = supabase
      .channel('distribution-logs-changes')
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'distribution_logs',
          filter: `tenant_id=eq.${tenantId}`,
        },
        () => {
          logger.info('DistributionMonitor', 'Nova distribuição recebida');
          
          // Atualiza listas
          fetchStats();
          fetchRecentDistributions();
          fetchHourlyVolume();
        }
      )
      .subscribe();

    return () => {
      logger.info('DistributionMonitor', 'Removendo subscription de distribuições');
      supabase.removeChannel(channel);
    };
  }, [tenantId, fetchStats, fetchRecentDistributions, fetchHourlyVolume]);

  // Subscription para mudanças de status de agentes
  useEffect(() => {
    if (!tenantId) return;

    logger.info('DistributionMonitor', 'Iniciando subscription real-time para status de agentes');

    const channel = supabase
      .channel('agent-status-changes')
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'tenant_members',
          filter: `tenant_id=eq.${tenantId}`,
        },
        () => {
          logger.info('DistributionMonitor', 'Mudança de status de agente');
          
          // Atualiza listas
          fetchStats();
          fetchAgentStatuses();
          fetchDepartments();
          fetchQueues();
        }
      )
      .subscribe();

    return () => {
      logger.info('DistributionMonitor', 'Removendo subscription de agentes');
      supabase.removeChannel(channel);
    };
  }, [tenantId, fetchStats, fetchAgentStatuses, fetchDepartments, fetchQueues]);

  // ==============================================================
  // Retorno do Hook
  // ==============================================================

  // Filtra distribuições recentes pelo tipo de conversa (compatibilidade)
  const filteredDistributions = recentDistributions.filter(d => {
    // Primeiro filtra por tipo de entidade
    if (entityFilter !== 'all' && d.entityType !== entityFilter) {
      return false;
    }
    // Depois filtra por tipo de conversa (apenas para conversas)
    if (typeFilter === 'all') return true;
    if (d.entityType === 'deal') return true; // Deals passam o filtro de conversa
    return d.conversationType === typeFilter;
  });

  return {
    // Dados
    stats,
    agentStatuses,
    departments,
    queues, // Novo: filas globais
    recentDistributions,
    filteredDistributions,
    hourlyVolume,

    // Estado
    isLoading,
    error,
    typeFilter,
    entityFilter, // Novo: filtro por tipo de entidade

    // Ações
    refresh,
    setTypeFilter,
    setEntityFilter, // Novo: setter para filtro de entidade
  };
}
